import React, { useState, useEffect } from 'react';
import { approvalPoliciesApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { Loader2, Plus, Pencil, Trash2, Users, AlertCircle, X } from 'lucide-react';

/**
 * Approval groups — a named set of people who can sign leave off, e.g. "Directors".
 *
 * Selectable anywhere a single approver can be: an employee's "Approved by", their
 * "Escalates to", or a policy level. The point is that maintenance lives in ONE place:
 * a director leaves and you edit the group, not every employee who listed them.
 *
 * Only managers and admins can be members — a group isn't a way round the rule that
 * only they can approve. Existing members who've since been archived or demoted are
 * shown and flagged rather than quietly dropped, because "Directors" being down to one
 * usable person is exactly the sort of thing that otherwise surfaces when a request
 * goes nowhere.
 */
const APPROVING_ROLES = ['manager', 'admin', 'superadmin'];

const ApprovalGroupsManager = ({ people = [], onChanged }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);   // group object, or {} for a new one
  const [form, setForm] = useState({ name: '', description: '', member_ids: [] });

  const eligible = people.filter((p) =>
    p.email && !p.is_archived && APPROVING_ROLES.includes(p.role));

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await approvalPoliciesApi.listApprovalGroups();
      setGroups(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load approval groups');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing({});
    setForm({ name: '', description: '', member_ids: [] });
  };

  const startEdit = (g) => {
    setEditing(g);
    setForm({
      name: g.name,
      description: g.description || '',
      member_ids: (g.members || []).map((m) => m.id),
    });
  };

  const toggleMember = (id) => setForm((f) => ({
    ...f,
    member_ids: f.member_ids.includes(id)
      ? f.member_ids.filter((m) => m !== id)
      : [...f.member_ids, id],
  }));

  const save = async () => {
    if (!form.name.trim()) { toast.error('Give the group a name'); return; }
    setSaving(true);
    try {
      if (editing?.id) await approvalPoliciesApi.updateApprovalGroup(editing.id, form);
      else await approvalPoliciesApi.createApprovalGroup(form);
      toast.success(editing?.id ? 'Group updated' : 'Group created');
      setEditing(null);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save the group');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? Anyone using it will need a new approver.`)) return;
    try {
      await approvalPoliciesApi.deleteApprovalGroup(g.id);
      toast.success(`${g.name} deleted`);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      // The API refuses while something still points at the group, and says what.
      toast.error(e.response?.data?.detail || 'Could not delete the group');
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-muted-foreground p-6">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <Users className="w-4 h-4" /> Approval groups
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            A named set of people, any one of whom can sign off a request — "any of the
            directors", say. Pick a group anywhere you'd pick a single approver. When
            someone leaves you update the group once, rather than every person who
            listed them.
          </p>
        </div>
        <Button onClick={startNew} className="shrink-0" data-testid="new-approval-group">
          <Plus className="w-4 h-4 mr-2" /> New group
        </Button>
      </div>

      {groups.length === 0 && !editing && (
        <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          No groups yet. Handy when several people can approve the same requests.
        </div>
      )}

      <div className="space-y-3">
        {groups.map((g) => (
          <div key={g.id} className="rounded-xl border border-border bg-card p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2">
                  {g.name}
                  {g.usable_members === 0 && (
                    <span className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> nobody in here can approve
                    </span>
                  )}
                </div>
                {g.description && (
                  <p className="text-sm text-muted-foreground mt-0.5">{g.description}</p>
                )}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(g.members || []).length === 0 && (
                    <span className="text-xs text-muted-foreground">No members yet</span>
                  )}
                  {(g.members || []).map((m) => (
                    <span
                      key={m.id}
                      className={`text-xs px-2 py-0.5 rounded-full border ${
                        m.can_approve
                          ? 'border-border bg-secondary/50'
                          : 'border-red-500/40 bg-red-500/10 text-red-600'
                      }`}
                      title={m.can_approve ? '' : "Can't approve leave any more"}
                    >
                      {m.name}{m.can_approve ? '' : ' — can’t approve'}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {g.in_use === 0
                    ? 'Not used anywhere yet'
                    : `Used by ${g.in_use} ${g.in_use === 1 ? 'person or policy level' : 'people and policy levels'}`}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button variant="ghost" size="sm" onClick={() => startEdit(g)} aria-label={`Edit ${g.name}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => remove(g)} aria-label={`Delete ${g.name}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="rounded-xl border border-primary/40 bg-card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">{editing.id ? `Edit ${editing.name}` : 'New approval group'}</h4>
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)} aria-label="Close">
              <X className="w-4 h-4" />
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                className="input-hive mt-1"
                value={form.name}
                placeholder="Directors"
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Input
                className="input-hive mt-1"
                value={form.description}
                placeholder="Any of the directors can sign off"
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Who's in it?</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">
              Only managers and admins are listed — a group can't give someone the
              ability to approve. You can narrow what a manager may do in
              Settings → Manager permissions.
            </p>
            {eligible.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Nobody available. Make someone a manager first.
              </p>
            ) : (
              <div className="max-h-52 overflow-y-auto rounded-lg border border-border divide-y divide-border">
                {eligible.map((p) => (
                  <label key={p.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-secondary/40">
                    <input
                      type="checkbox"
                      checked={form.member_ids.includes(p.id)}
                      onChange={() => toggleMember(p.id)}
                      className="w-4 h-4 rounded border-input accent-primary"
                    />
                    <span className="text-sm">{p.first_name} {p.last_name}</span>
                    <span className="text-xs text-muted-foreground ml-auto capitalize">{p.role}</span>
                  </label>
                ))}
              </div>
            )}
            {form.member_ids.length === 1 && (
              <p className="text-xs text-muted-foreground mt-2">
                One member behaves just like picking that person directly. Two or more is
                where a group earns its keep — including when one of them is the person
                asking for the leave.
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editing.id ? 'Save changes' : 'Create group'}
            </Button>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalGroupsManager;
